import _slicedToArray from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _asyncToGenerator from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _toConsumableArray from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import _regeneratorRuntime from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ChatMessageCardItemTypeEnum } from '@jove-frontend/types';
import { AndroidOutlined, SupportAgentOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import clsx from 'clsx';
import { apiChatSwitchToHumanSupportService, apiChatSwitchToJoveAIService } from '../../api/chat/mode';
import { JoveChatMessageResponseModeEnum } from '../../enums/chat';
import { covertServerChatPayloadToChatMessageItemInfo } from '../../utils/chat/chatMessage';
import AIChatAnalyzingIndicator from './components/analyzingIndicator';
import CardWrapper from './components/cardWrapper';
import CommonCard from './components/commonCard';
import PriceInfoCard from './components/priceInfoCard';
import SwitchModeTipCard from './components/switchModeTipCard';
import InputView from './inputView';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var AIChatWindowChatMessageBoxView = /*#__PURE__*/forwardRef(function (props, ref) {
  var className = props.className,
      chatUserInfo = props.chatUserInfo,
      sendMessage = props.sendMessage,
      buttonClick = props.buttonClick,
      linkClick = props.linkClick,
      _props$showAnalyzingI = props.showAnalyzingIndicator,
      showAnalyzingIndicator = _props$showAnalyzingI === void 0 ? false : _props$showAnalyzingI,
      messageListChange = props.messageListChange,
      list = props.list,
      hideHumanSupport = props.hideHumanSupport,
      onReachTop = props.onReachTop,
      _props$canLoadMoreHis = props.canLoadMoreHistory,
      canLoadMoreHistory = _props$canLoadMoreHis === void 0 ? false : _props$canLoadMoreHis,
      _props$isLoadingMoreH = props.isLoadingMoreHistory,
      isLoadingMoreHistory = _props$isLoadingMoreH === void 0 ? false : _props$isLoadingMoreH,
      chatModeChange = props.chatModeChange,
      hideInputView = props.hideInputView,
      chatResponseMode = props.chatResponseMode,
      inputLoading = props.inputLoading;

  var _useState = useState(list || []),
      messageList = _useState[0],
      setMessageList = _useState[1];

  var _useState2 = useState(false),
      isHumanSupportMode = _useState2[0],
      setIsHumanSupportMode = _useState2[1];

  var _useState3 = useState(false),
      isSupportModeSwitching = _useState3[0],
      setIsSupportModeSwitching = _useState3[1];

  var inputViewRef = useRef(null);
  var messageListContainerRef = useRef(null);
  var messagesEndRef = useRef(null);
  var shouldScrollToBottomRef = useRef(true);
  var isTopCallbackLockedRef = useRef(false);
  /**
   * effects
   */

  useEffect(function () {
    if (showAnalyzingIndicator) {
      scrollToBottom();
    }
  }, [showAnalyzingIndicator]);
  useEffect(function () {
    if (!isLoadingMoreHistory) {
      isTopCallbackLockedRef.current = false;
    }
  }, [isLoadingMoreHistory]);
  useEffect(function () {
    setMessageList(list || []);
  }, [list]);
  useEffect(function () {
    if (chatResponseMode) {
      setIsHumanSupportMode(chatResponseMode === JoveChatMessageResponseModeEnum.agent);
    }
  }, [chatResponseMode]);
  /**
   * methods
   */

  var submitMessage = function submitMessage(item) {
    sendMessage === null || sendMessage === void 0 ? void 0 : sendMessage(item);
  };

  var scrollToBottom = function scrollToBottom() {
    var _messagesEndRef$curre;

    (_messagesEndRef$curre = messagesEndRef.current) === null || _messagesEndRef$curre === void 0 ? void 0 : _messagesEndRef$curre.scrollIntoView();
  };

  var updateMessageList = function updateMessageList(list) {
    messageListChange === null || messageListChange === void 0 ? void 0 : messageListChange(list);

    if (shouldScrollToBottomRef.current) {
      scrollToBottom();
    }
  };
  /**
   * external methods
   */


  useImperativeHandle(ref, function () {
    return {
      cleanInputInfo: cleanInputInfo,
      appendMessageList: appendMessageList,
      prependMessageList: prependMessageList,
      scrollToBottom: scrollToBottom
    };
  });

  var cleanInputInfo = function cleanInputInfo() {
    var _inputViewRef$current;

    (_inputViewRef$current = inputViewRef.current) === null || _inputViewRef$current === void 0 ? void 0 : _inputViewRef$current.cleanInputInfo();
  };

  var appendMessageList = function appendMessageList(list) {
    shouldScrollToBottomRef.current = true;
    updateMessageList([].concat(_toConsumableArray(messageList), _toConsumableArray(list || [])));
  };

  var prependMessageList = function prependMessageList(list) {
    if (!(list !== null && list !== void 0 && list.length)) {
      return;
    }

    shouldScrollToBottomRef.current = false;
    var container = messageListContainerRef.current;
    var prevScrollHeight = (container === null || container === void 0 ? void 0 : container.scrollHeight) || 0;
    updateMessageList([].concat(_toConsumableArray(list || []), _toConsumableArray(messageList)));
    requestAnimationFrame(function () {
      if (!container) {
        return;
      }

      var nextScrollHeight = container.scrollHeight;
      container.scrollTop += nextScrollHeight - prevScrollHeight;
    });
  };

  var handleMessageListScroll = function handleMessageListScroll() {
    var container = messageListContainerRef.current;

    if (!container) {
      return;
    }

    if (container.scrollTop > 8) {
      isTopCallbackLockedRef.current = false;
      return;
    }

    if (isTopCallbackLockedRef.current || isLoadingMoreHistory || !canLoadMoreHistory) {
      return;
    }

    isTopCallbackLockedRef.current = true;
    onReachTop === null || onReachTop === void 0 ? void 0 : onReachTop();
  };

  var appendSupportModeSwitchedTip = function appendSupportModeSwitchedTip(mode) {
    var tipMessage = mode === 'ai' ? 'Mia has joined the conversation.' : 'Switched to manual service.';
    appendMessageList([covertServerChatPayloadToChatMessageItemInfo({
      body: tipMessage,
      options: [],
      card_type: ChatMessageCardItemTypeEnum.tipAlertText
    })]);
  };

  var toggleSupportMode = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var params, targetMode, _ref2, _ref3, _, err;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (!isSupportModeSwitching) {
                _context.next = 2;
                break;
              }

              return _context.abrupt("return");

            case 2:
              params = {
                email: chatUserInfo.email
              };

              if (chatUserInfo.phone) {
                params['phone'] = chatUserInfo.phone;
              }

              setIsSupportModeSwitching(true);
              targetMode = isHumanSupportMode ? 'ai' : 'human';

              if (!isHumanSupportMode) {
                _context.next = 12;
                break;
              }

              _context.next = 9;
              return apiChatSwitchToJoveAIService(params);

            case 9:
              _context.t0 = _context.sent;
              _context.next = 15;
              break;

            case 12:
              _context.next = 14;
              return apiChatSwitchToHumanSupportService(params);

            case 14:
              _context.t0 = _context.sent;

            case 15:
              _ref2 = _context.t0;
              _ref3 = _slicedToArray(_ref2, 2);
              _ = _ref3[0];
              err = _ref3[1];

              if (!err) {
                chatModeChange === null || chatModeChange === void 0 ? void 0 : chatModeChange(isHumanSupportMode ? JoveChatMessageResponseModeEnum.ai : JoveChatMessageResponseModeEnum.agent);
                appendSupportModeSwitchedTip(targetMode);
              }

              setIsSupportModeSwitching(false);
              requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                  scrollToBottom();
                });
              });

            case 22:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function toggleSupportMode() {
      return _ref.apply(this, arguments);
    };
  }();

  return /*#__PURE__*/_jsxs(Box, {
    className: clsx('relative flex flex-col', className),
    children: [/*#__PURE__*/_jsxs(Box, {
      ref: messageListContainerRef,
      className: "flex-1 space-y-3 overflow-y-auto px-4 py-4",
      onScroll: handleMessageListScroll,
      sx: {
        background: 'linear-gradient(180deg, #c8d1f2 0%, #f4eff1 100%)'
      },
      children: [isLoadingMoreHistory && /*#__PURE__*/_jsx(Box, {
        className: "sticky top-0 z-20 -mx-4 -mt-4 mb-3 flex items-center justify-center px-4 py-2",
        children: /*#__PURE__*/_jsxs(Box, {
          className: "inline-flex items-center rounded-full px-3 py-1",
          sx: {
            backgroundColor: 'rgba(255,255,255,0.88)',
            boxShadow: '0 2px 8px rgba(17, 24, 39, 0.12)'
          },
          children: [/*#__PURE__*/_jsx(CircularProgress, {
            size: 14,
            thickness: 5,
            className: "text-brand-secondary"
          }), /*#__PURE__*/_jsx(Typography, {
            className: "text-brand-secondary ml-2 text-[12px] font-medium",
            children: "Loading history..."
          })]
        })
      }), messageList.map(function (item, index) {
        var _item$list;

        var firstMessage = (_item$list = item.list) === null || _item$list === void 0 ? void 0 : _item$list[0];

        if ((firstMessage === null || firstMessage === void 0 ? void 0 : firstMessage.cardType) === ChatMessageCardItemTypeEnum.tipAlertText) {
          return /*#__PURE__*/_jsx(SwitchModeTipCard, {
            info: firstMessage
          }, firstMessage.id);
        }

        return /*#__PURE__*/_jsx(CardWrapper, {
          message: item,
          children: /*#__PURE__*/_jsx(Box, {
            className: "flex flex-col",
            children: (item.list || []).map(function (message) {
              // if (message?.cardType === ChatMessageCardItemTypeEnum.quotePrice) {
              if (!!(message !== null && message !== void 0 && message.pricingData)) {
                return /*#__PURE__*/_jsx(PriceInfoCard, {
                  message: message,
                  buttonClick: buttonClick
                }, message.id);
              }

              return /*#__PURE__*/_jsx(CommonCard, {
                message: message,
                buttonClick: buttonClick,
                linkClick: linkClick
              }, message.id);
            })
          })
        }, index);
      }), showAnalyzingIndicator && /*#__PURE__*/_jsx(AIChatAnalyzingIndicator, {}), /*#__PURE__*/_jsx("div", {
        ref: messagesEndRef
      })]
    }), !hideHumanSupport && /*#__PURE__*/_jsxs(Box, {
      className: clsx('group absolute bottom-20 right-4 z-10 flex items-center', {
        'cursor-pointer': !isSupportModeSwitching,
        'cursor-not-allowed opacity-60': isSupportModeSwitching
      }),
      onClick: toggleSupportMode,
      children: [/*#__PURE__*/_jsx(Typography, {
        className: "text-brand-secondary mr-2 max-w-0 -translate-x-2 overflow-hidden whitespace-nowrap text-[12px] opacity-0 transition-all duration-300 group-hover:max-w-[160px] group-hover:translate-x-0 group-hover:opacity-100",
        children: isHumanSupportMode ? 'Chat with Jove AI Mia' : 'Switch to human service'
      }), isHumanSupportMode ? /*#__PURE__*/_jsx(AndroidOutlined, {
        className: "text-brand-secondary h-7 w-7"
      }) : /*#__PURE__*/_jsx(SupportAgentOutlined, {
        className: "text-brand-secondary h-7 w-7"
      })]
    }), !hideInputView && /*#__PURE__*/_jsx(InputView, {
      ref: inputViewRef,
      sendMessage: submitMessage,
      loading: inputLoading
    })]
  });
});
export default AIChatWindowChatMessageBoxView;